import {
  ClientAdProductType,
  type MfRegionPerformanceResponseDto,
  type PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import type { CustomerAnalyticsListingFilterType } from '@zg-rentals/ts-customer-analytics-client';
import { ClientAdProductType_values } from '@zg-rentals/ts-customer-analytics-client';
import { format } from 'date-fns';
import { allTimestampsOneYear } from '../../../../utils/allTimestampsOneYear';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import { PACKAGE_COLORS } from '../../../SharedComponents/Map/Dots/Dots.consts';
import { BuildingType } from './OverviewReport.types';

export type ProspectsChartData = {
  name: string;
  'Total direct leads': number;
  'Total listing web referrals': number;
  'Avg. direct leads': number;
  'Avg. listing web referrals': number;
  'Median market listing prospects': number | null;
  'Median market listing prospects month to date': number | null;
  'Month to date direct leads': number;
  'Month to date listing web referrals': number;
  'Month to date avg. direct leads': number;
  'Month to date avg. listing web referrals': number;
  numPropertiesPerMonth: number;
  timestamp: number;
  isCurrentMonth?: boolean;
};

type GenerateProspectsDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  buildingType: BuildingType;
  regionPerformanceData?: Array<MfRegionPerformanceResponseDto>;
  packageFilter?: ClientAdProductType | CustomerAnalyticsListingFilterType | '';
  hasSelectedProperty?: boolean;
};

export const generateProspectsData = ({
  filteredProperties,
  buildingType,
  regionPerformanceData,
  packageFilter,
  hasSelectedProperty,
}: GenerateProspectsDataParams): Array<ProspectsChartData> => {
  const timestamps = allTimestampsOneYear();
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const isSingleFamily = buildingType === BuildingType.SINGLE_FAMILY;
  const isSpotlight = packageFilter === ClientAdProductType.LISTING_SPOTLIGHT;
  const chartDataMap = new Map<string, ProspectsChartData>();

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    const isCurrentMonth = formattedDate === currentDateFormatted;
    chartDataMap.set(formattedDate, {
      name: formattedDate,
      'Total direct leads': 0,
      'Total listing web referrals': 0,
      'Avg. direct leads': 0,
      'Avg. listing web referrals': 0,
      'Median market listing prospects': 0,
      'Median market listing prospects month to date': 0,
      'Month to date direct leads': 0,
      'Month to date listing web referrals': 0,
      'Month to date avg. direct leads': 0,
      'Month to date avg. listing web referrals': 0,
      numPropertiesPerMonth: 0,
      timestamp,
      isCurrentMonth,
    });
  });
  filteredProperties
    .filter((property) =>
      isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
    )
    .forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const statDate = localDateToDate(stat.statDate);
        const formattedDate = format(statDate, 'MMM yyyy');
        const currentData = chartDataMap.get(formattedDate);
        if (currentData) {
          let directLeads: number;
          let webReferrals: number;

          const isPackageType =
            !hasSelectedProperty &&
            !isSpotlight &&
            packageFilter &&
            (ClientAdProductType_values as ReadonlyArray<string>).includes(packageFilter);
          if (isPackageType) {
            const packageTotal = stat.totalsByProductTypeAndDate?.find(
              (total) => total.adProductType === packageFilter,
            );
            directLeads = packageTotal?.leadsTotal ?? 0;
            webReferrals = packageTotal?.listingWebReferrals ?? 0;
          } else {
            directLeads = stat.leadCount;
            webReferrals = stat.listingWebReferrals ?? 0;
          }

          if (currentData.isCurrentMonth) {
            currentData['Month to date direct leads'] += directLeads;
            currentData['Month to date listing web referrals'] += webReferrals;
          } else {
            currentData['Total direct leads'] += directLeads;
            currentData['Total listing web referrals'] += webReferrals;
          }
          currentData.numPropertiesPerMonth += 1;
        }
      });
    });

  regionPerformanceData?.forEach((data) => {
    const statDate = localDateToDate(data.dataDate);
    const formattedDate = format(statDate, 'MMM yyyy');
    const currentData = chartDataMap.get(formattedDate);
    if (currentData) {
      currentData['Median market listing prospects']! += isSingleFamily
        ? data.medianProspects
        : data.listingProspectsMedian;
    }
  });

  const chartData = Array.from(chartDataMap.values());
  return chartData
    .map((value, index) => {
      const isLast = index === chartData.length - 1;

      return {
        ...value,
        'Avg. direct leads':
          value.numPropertiesPerMonth > 0 ? value['Total direct leads'] / value.numPropertiesPerMonth : 0,
        'Avg. listing web referrals':
          value.numPropertiesPerMonth > 0 ? value['Total listing web referrals'] / value.numPropertiesPerMonth : 0,
        'Month to date avg. direct leads':
          value.numPropertiesPerMonth > 0 ? value['Month to date direct leads'] / value.numPropertiesPerMonth : 0,
        'Month to date avg. listing web referrals':
          value.numPropertiesPerMonth > 0
            ? value['Month to date listing web referrals'] / value.numPropertiesPerMonth
            : 0,
        'Median market listing prospects': !isLast ? value['Median market listing prospects'] : null,
        'Median market listing prospects month to date': null,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
};

export type PageViewsChartData = {
  name: string;
  'Portfolio total': number;
  'Avg. page views': number;
  'Median market page views': number | null;
  'Month to date portfolio total': number;
  'Month to date median page views': number;
  numPropertiesPerMonth: number;
  timestamp: number;
  isCurrentMonth?: boolean;
};

export type ImpressionsChartData = {
  name: string;
  'Portfolio total': number;
  'Avg. impressions': number;
  'Median market impressions': number | null;
  'Month to date portfolio total': number;
  'Month to date median impressions': number;
  numPropertiesPerMonth: number;
  timestamp: number;
  isCurrentMonth?: boolean;
};

export type ActivePropertiesChartData = {
  name: string;
  'Portfolio total': number;
  'Market total': number;
  'Month to date portfolio total': number;
  'Month to date market total': number;
  timestamp: number;
  isCurrentMonth?: boolean;
};

type GenerateDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  buildingType: BuildingType;
  regionPerformanceData?: Array<MfRegionPerformanceResponseDto>;
  packageFilter?: ClientAdProductType | CustomerAnalyticsListingFilterType | '';
  hasSelectedProperty?: boolean;
};

export const generatePageViewsData = ({
  filteredProperties,
  buildingType,
  regionPerformanceData,
  packageFilter,
  hasSelectedProperty,
}: GenerateDataParams): Array<PageViewsChartData> => {
  const timestamps = allTimestampsOneYear();
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const isSingleFamily = buildingType === BuildingType.SINGLE_FAMILY;
  const isSpotlight = packageFilter === ClientAdProductType.LISTING_SPOTLIGHT;
  const chartDataMap = new Map<string, PageViewsChartData>();

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    const isCurrentMonth = formattedDate === currentDateFormatted;
    chartDataMap.set(formattedDate, {
      name: formattedDate,
      'Portfolio total': 0,
      'Avg. page views': 0,
      'Median market page views': 0,
      'Month to date portfolio total': 0,
      'Month to date median page views': 0,
      numPropertiesPerMonth: 0,
      timestamp,
      isCurrentMonth,
    });
  });

  filteredProperties
    .filter((property) =>
      isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
    )
    .forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const statDate = localDateToDate(stat.statDate);
        const formattedDate = format(statDate, 'MMM yyyy');
        const currentData = chartDataMap.get(formattedDate);
        if (currentData) {
          const isPackageType =
            !hasSelectedProperty &&
            !isSpotlight &&
            packageFilter &&
            (ClientAdProductType_values as ReadonlyArray<string>).includes(packageFilter);
          let views: number;
          if (isPackageType) {
            const packageTotal = stat.totalsByProductTypeAndDate?.find(
              (total) => total.adProductType === packageFilter,
            );
            views = packageTotal?.viewsTotal ?? 0;
          } else {
            views = stat.viewCount;
          }

          if (currentData.isCurrentMonth) {
            currentData['Month to date portfolio total'] += views;
          } else {
            currentData['Portfolio total'] += views;
          }
          currentData.numPropertiesPerMonth += 1;
        }
      });
    });

  regionPerformanceData?.forEach((data) => {
    const statDate = localDateToDate(data.dataDate);
    const formattedDate = format(statDate, 'MMM yyyy');
    const currentData = chartDataMap.get(formattedDate);
    if (currentData) {
      currentData['Median market page views']! += data.medianViews;
    }
  });

  const chartData = Array.from(chartDataMap.values());
  return chartData
    .map((value, index) => {
      const isLast = index === chartData.length - 1;

      return {
        ...value,
        'Avg. page views':
          value.numPropertiesPerMonth > 0 ? value['Portfolio total'] / (value.numPropertiesPerMonth || 1) : 0,
        'Month to date median page views':
          value.numPropertiesPerMonth > 0
            ? value['Month to date portfolio total'] / (value.numPropertiesPerMonth || 1)
            : 0,
        'Median market page views': !isLast ? value['Median market page views'] : null,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
};

export const generateImpressionsData = ({
  filteredProperties,
  buildingType,
  regionPerformanceData,
  packageFilter,
  hasSelectedProperty,
}: GenerateDataParams): Array<ImpressionsChartData> => {
  const timestamps = allTimestampsOneYear();
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const isSingleFamily = buildingType === BuildingType.SINGLE_FAMILY;
  const isSpotlight = packageFilter === ClientAdProductType.LISTING_SPOTLIGHT;
  const chartDataMap = new Map<string, ImpressionsChartData>();

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    const isCurrentMonth = formattedDate === currentDateFormatted;
    chartDataMap.set(formattedDate, {
      name: formattedDate,
      'Portfolio total': 0,
      'Avg. impressions': 0,
      'Median market impressions': 0,
      'Month to date portfolio total': 0,
      'Month to date median impressions': 0,
      numPropertiesPerMonth: 0,
      timestamp,
      isCurrentMonth,
    });
  });

  filteredProperties
    .filter((property) =>
      isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
    )
    .forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const statDate = localDateToDate(stat.statDate);
        const formattedDate = format(statDate, 'MMM yyyy');
        const currentData = chartDataMap.get(formattedDate);
        if (currentData) {
          const isPackageType =
            !hasSelectedProperty &&
            !isSpotlight &&
            packageFilter &&
            (ClientAdProductType_values as ReadonlyArray<string>).includes(packageFilter);
          let impressions: number;
          if (isPackageType) {
            const packageTotal = stat.totalsByProductTypeAndDate?.find(
              (total) => total.adProductType === packageFilter,
            );
            impressions = packageTotal?.impressionsTotal ?? 0;
          } else {
            impressions = stat.impressionCount;
          }

          if (currentData.isCurrentMonth) {
            currentData['Month to date portfolio total'] += impressions;
          } else {
            currentData['Portfolio total'] += impressions;
          }
          currentData.numPropertiesPerMonth += 1;
        }
      });
    });

  regionPerformanceData?.forEach((data) => {
    const statDate = localDateToDate(data.dataDate);
    const formattedDate = format(statDate, 'MMM yyyy');
    const currentData = chartDataMap.get(formattedDate);
    if (currentData) {
      currentData['Median market impressions']! += data.medianImpressions;
    }
  });

  const chartData = Array.from(chartDataMap.values());
  return chartData
    .map((value, index) => {
      const isLast = index === chartData.length - 1;

      return {
        ...value,
        'Avg. impressions':
          value.numPropertiesPerMonth > 0 ? value['Portfolio total'] / (value.numPropertiesPerMonth || 1) : 0,
        'Month to date median impressions':
          value.numPropertiesPerMonth > 0
            ? value['Month to date portfolio total'] / (value.numPropertiesPerMonth || 1)
            : 0,
        'Median market impressions': !isLast ? value['Median market impressions'] : null,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
};

export const generateActivePropertiesData = ({
  filteredProperties,
  buildingType,
  regionPerformanceData,
  packageFilter,
  hasSelectedProperty,
}: GenerateDataParams): Array<ActivePropertiesChartData> => {
  const timestamps = allTimestampsOneYear();
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const isSingleFamily = buildingType === BuildingType.SINGLE_FAMILY;
  const isSpotlight = packageFilter === ClientAdProductType.LISTING_SPOTLIGHT;
  const dataMap = new Map<string, ActivePropertiesChartData>();

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    const isCurrentMonth = formattedDate === currentDateFormatted;
    dataMap.set(formattedDate, {
      name: formattedDate,
      'Portfolio total': 0,
      'Market total': 0,
      'Month to date portfolio total': 0,
      'Month to date market total': 0,
      timestamp,
      isCurrentMonth,
    });
  });

  filteredProperties
    .filter((property) =>
      isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
    )
    .forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const statDate = localDateToDate(stat.statDate);
        const formattedDate = format(statDate, 'MMM yyyy');
        if (dataMap.has(formattedDate)) {
          const isPackageType =
            !hasSelectedProperty &&
            !isSpotlight &&
            packageFilter &&
            (ClientAdProductType_values as ReadonlyArray<string>).includes(packageFilter);
          if (isPackageType) {
            const hasPackage = stat.totalsByProductTypeAndDate?.some((total) => total.adProductType === packageFilter);
            if (!hasPackage) return;
          }

          const currentData = dataMap.get(formattedDate)!;
          if (currentData.isCurrentMonth) {
            currentData['Month to date portfolio total'] += 1;
          } else {
            currentData['Portfolio total'] += 1;
          }
        }
      });
    });

  regionPerformanceData?.forEach((data) => {
    const formattedDate = format(localDateToDate(data.dataDate), 'MMM yyyy');
    if (dataMap.has(formattedDate)) {
      const currentData = dataMap.get(formattedDate)!;
      if (currentData.isCurrentMonth) {
        currentData['Month to date market total'] = data.totalListings;
      } else {
        currentData['Market total'] = data.totalListings;
      }
    }
  });

  return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export type PackageBreakdownEntry = {
  displayName: string;
  color: string;
  packageType: ClientAdProductType;
  prospectsKey: string;
  mtdProspectsKey: string;
  leadsKey: string;
  mtdLeadsKey: string;
  propertiesKey: string;
  mtdPropertiesKey: string;
};

export type ProspectsPackageBreakdownResult = {
  chartData: Array<Record<string, number | string | boolean | undefined>>;
  packages: Array<PackageBreakdownEntry>;
};

type GenerateProspectsPackageBreakdownParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  buildingType: BuildingType;
  packageFilter?: ClientAdProductType | CustomerAnalyticsListingFilterType | '';
  hasSelectedProperty?: boolean;
};

export const generateProspectsPackageBreakdownData = ({
  filteredProperties,
  buildingType,
  packageFilter,
  hasSelectedProperty,
}: GenerateProspectsPackageBreakdownParams): ProspectsPackageBreakdownResult => {
  const timestamps = allTimestampsOneYear();
  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const isSingleFamily = buildingType === BuildingType.SINGLE_FAMILY;
  const isSpotlight = packageFilter === ClientAdProductType.LISTING_SPOTLIGHT;
  const chartDataMap = new Map<string, Record<string, number | string | boolean | undefined>>();
  const packageSet = new Map<ClientAdProductType, string>();

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    const isCurrentMonth = formattedDate === currentDateFormatted;
    chartDataMap.set(formattedDate, {
      name: formattedDate,
      timestamp,
      isCurrentMonth,
    });
  });

  filteredProperties
    .filter((property) =>
      isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
    )
    .forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const statDate = localDateToDate(stat.statDate);
        const formattedDate = format(statDate, 'MMM yyyy');
        const currentData = chartDataMap.get(formattedDate);
        if (!currentData) return;

        const packageType = stat.currentAdproductType;
        const displayName = mapPackageTypeName(packageType);
        if (displayName !== 'Unknown') packageSet.set(packageType, displayName);

        const propertiesKey = `${displayName}_properties`;
        const mtdPropertiesKey = `${displayName} MTD_properties`;
        const leadsKey = `${displayName}_leads`;
        const mtdLeadsKey = `${displayName} MTD_leads`;

        const isPackageType =
          !hasSelectedProperty &&
          !isSpotlight &&
          packageFilter &&
          (ClientAdProductType_values as ReadonlyArray<string>).includes(packageFilter);
        let prospects: number;
        if (isPackageType) {
          const packageTotal = stat.totalsByProductTypeAndDate?.find((total) => total.adProductType === packageFilter);
          prospects = (packageTotal?.leadsTotal ?? 0) + (packageTotal?.listingWebReferrals ?? 0);
        } else {
          prospects = stat.leadCount + (stat.listingWebReferrals ?? 0);
        }

        if (currentData.isCurrentMonth) {
          currentData[mtdPropertiesKey] = ((currentData[mtdPropertiesKey] as number) || 0) + 1;
          currentData[mtdLeadsKey] = ((currentData[mtdLeadsKey] as number) || 0) + prospects;
        } else {
          currentData[propertiesKey] = ((currentData[propertiesKey] as number) || 0) + 1;
          currentData[leadsKey] = ((currentData[leadsKey] as number) || 0) + prospects;
        }
      });
    });

  chartDataMap.forEach((currentData) => {
    let totalProperties = 0;
    let totalProspects = 0;
    let totalMtdProperties = 0;
    let totalMtdProspects = 0;

    for (const [, displayName] of packageSet) {
      totalProperties += (currentData[`${displayName}_properties`] as number) || 0;
      totalProspects += (currentData[`${displayName}_leads`] as number) || 0;
      totalMtdProperties += (currentData[`${displayName} MTD_properties`] as number) || 0;
      totalMtdProspects += (currentData[`${displayName} MTD_leads`] as number) || 0;
    }

    for (const [, displayName] of packageSet) {
      const pkgProperties = (currentData[`${displayName}_properties`] as number) || 0;
      const pkgMtdProperties = (currentData[`${displayName} MTD_properties`] as number) || 0;

      currentData[displayName] = totalProperties > 0 ? totalProspects * (pkgProperties / totalProperties) : 0;
      currentData[`${displayName} MTD`] =
        totalMtdProperties > 0 ? totalMtdProspects * (pkgMtdProperties / totalMtdProperties) : 0;
    }
  });

  const packages: Array<PackageBreakdownEntry> = Array.from(packageSet.entries()).map(([packageType, displayName]) => ({
    displayName,
    color: PACKAGE_COLORS[packageType] || '#888888',
    packageType,
    prospectsKey: displayName,
    mtdProspectsKey: `${displayName} MTD`,
    leadsKey: `${displayName}_leads`,
    mtdLeadsKey: `${displayName} MTD_leads`,
    propertiesKey: `${displayName}_properties`,
    mtdPropertiesKey: `${displayName} MTD_properties`,
  }));

  const chartData = Array.from(chartDataMap.values()).sort((a, b) => (a.timestamp as number) - (b.timestamp as number));

  return { chartData, packages };
};
